import { APIModel } from "./apiModel.ts";
import { initMqtt, disconnectMqtt } from "./mqttClient.ts";
// global state
import { globalState } from "../framework/globalState.ts";
import type { IUser, ISite, IDevice, IRegister, IAlarm, IFleetSiteKPI } from '../types/index.ts';

const apiModel = new APIModel();
const API = apiModel.getEndpoints();

// ⚠️ TOKEN SECURITY STRATEGY:
// - Access Token (1 day): Stored in memory (fast, XSS-safe)
// - Refresh Token (7 days): Stored in localStorage (survives page reload)
// - Auto-refresh access token before it expires
// - Handle 401 → refresh token → retry request pattern
// This balance XSS safety with session persistence

let accessToken: string | null = null;  // 🔐 In-memory access token (not in localStorage)

function unwrapApiEnvelope<T = unknown>(payload: any): T {
  if (payload && typeof payload === 'object' && 'success' in payload) {
    if (payload.success === false) {
      throw new Error(payload.error || 'API request failed');
    }
    return ('data' in payload ? payload.data : payload) as T;
  }
  return payload as T;
}

async function parseResponseBody<T = unknown>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    const json = await res.json();
    return unwrapApiEnvelope<T>(json);
  }

  const text = await res.text();
  return text as unknown as T;
}

// ----------------- TOKEN MANAGEMENT -----------------
export function setAccessToken(token: string | null): void {
  accessToken = token;
  // Also update globalState for immediate use
  globalState.token = token;
}

export function getAccessToken(): string | null {
  return accessToken || (globalState.token as string | null);
}

export function setRefreshToken(token: string): void {
  localStorage.setItem("refreshToken", token);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem("refreshToken");
}

export function clearTokens(): void {
  accessToken = null;
  globalState.token = null;
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("token");  // Clear old localStorage token
}

// ----------------- AUTH -----------------
export async function login(username: string, password: string): Promise<{ user: IUser; token: string }> {
  const res = await fetch(API.auth.login, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    // include product so backend can offer clearer messages (frontend MUST NOT be trusted for auth)
    body: JSON.stringify({ username, password, product: "vetra" }),
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Login failed: ${res.status} - ${errorText}`);
  }

  const data = unwrapApiEnvelope<any>(await res.json());
  const { accessToken, refreshToken } = data;

  // 💾 Store tokens securely:
  // - Access token: in-memory only (short-lived, XSS-safe)
  // - Refresh token: localStorage (long-lived, revocable)
  setAccessToken(accessToken);
  setRefreshToken(refreshToken);
  globalState.token = accessToken;

  // Fetch user data using the new access token
  const user = await fetchData<IUser>(API.auth.me);
  globalState.user = user;
  localStorage.setItem("user", JSON.stringify(user));

  // 🔄 Initialize MQTT in background (non-blocking)
  // User can navigate even if MQTT fails; it will retry automatically
  initMqtt(accessToken).catch((err) => {
    console.warn("⚠️ MQTT connection failed, will retry in background:", err.message);
  });

  return { user, token: accessToken };
}

export async function usercycle(): Promise<void> {
  await fetchData(API.auth.me);
}

export function logout(): void {
  disconnectMqtt();
  clearTokens();  // Clear both access & refresh tokens
  localStorage.removeItem("user");
  localStorage.removeItem("currentSite");
  localStorage.removeItem("registers");
  globalState.currentSite = null;
  globalState.registers = [];
  globalState.devices = [];
  window.location.hash = "/";
}

export async function registerUser(userData: Record<string, unknown>): Promise<unknown> {
  return fetchData(API.auth.register, { method: "POST", body: JSON.stringify(userData) });
}

export async function updateCurrentUser(userData: Record<string, unknown>): Promise<unknown> {
  const userId = (globalState.user as any)?.id || (globalState.user as any)?._id;

  // Preferred on apiserver (users route). Keep legacy auth route fallback.
  if (userId) {
    return fetchData(API.auth.users.update(userId), { method: "PATCH", body: JSON.stringify(userData) });
  }

  return fetchData(API.auth.update, { method: "PUT", body: JSON.stringify(userData) });
}

export async function fetchAllUsers(): Promise<IUser[]> {
  return fetchData(API.auth.users.list);
}

export async function updateUser(userId: string, userData: Record<string, unknown>): Promise<unknown> {
  return fetchData(API.auth.users.update(userId), { method: "PATCH", body: JSON.stringify(userData) });
}

// ⚡ Auto-refresh access token using refresh token
async function refreshAccessToken(): Promise<string> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  try {
    const res = await fetch(API.auth.refresh, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });

    if (!res.ok) {
      // Refresh token is invalid or expired - user must login again
      clearTokens();
      throw new Error("Session expired - please login again");
    }

    const data = unwrapApiEnvelope<any>(await res.json());
    setAccessToken(data.accessToken);
    
    // 🔄 TOKEN ROTATION: Backend sends new refresh token on each refresh
    // Replace old refresh token with new one
    if (data.refreshToken) {
      setRefreshToken(data.refreshToken);
      console.log("🔄 Access & refresh tokens rotated successfully");
    } else {
      console.log("🔄 Access token refreshed successfully");
    }
    
    return data.accessToken;
  } catch (err) {
    console.error("❌ Token refresh failed:", (err as Error).message);
    clearTokens();
    logout();
    throw err;
  }
}

// ⚡ Flag to prevent infinite refresh loops
let isRefreshing = false;
interface PendingRequest {
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}
let pendingRequests: PendingRequest[] = [];

// Queue requests while token is being refreshed
function queueRequest(resolve: (value?: unknown) => void, reject: (reason?: unknown) => void): void {
  pendingRequests.push({ resolve, reject });
}

// Process queued requests after token refresh
function processPendingRequests(error?: unknown): void {
  pendingRequests.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  pendingRequests = [];
}

const isLocalDevHost = () => {
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
};

function toSameOriginPath(endpoint: string): string | null {
  try {
    const parsed = new URL(endpoint, window.location.origin);
    if (parsed.origin === window.location.origin) return parsed.toString();
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return null;
  }
}

function buildUrl(endpoint: string): URL {
  return new URL(endpoint, window.location.origin);
}

const getRequestCache = new Map<string, { expiresAt: number; data?: unknown; promise?: Promise<unknown> }>();
const rateLimitCooldown = new Map<string, number>();

const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

function isRateLimited(error: unknown): boolean {
  const message = String((error as any)?.message || error || '');
  return message.includes('429');
}

async function fetchDataCached<T = unknown>(endpoint: string, ttlMs = 4000): Promise<T> {
  const now = Date.now();
  const cached = getRequestCache.get(endpoint);
  const cooldownUntil = rateLimitCooldown.get(endpoint) || 0;

  if (cached?.data !== undefined && cooldownUntil > now) {
    return cached.data as T;
  }

  if (cached?.data !== undefined && cached.expiresAt > now) {
    return cached.data as T;
  }

  if (cached?.promise) {
    return cached.promise as Promise<T>;
  }

  const promise = (async () => {
    try {
      return await fetchData<T>(endpoint);
    } catch (error) {
      if (!isRateLimited(error)) throw error;

      // One delayed retry for transient rate-limit windows
      await wait(1200);
      try {
        return await fetchData<T>(endpoint);
      } catch (retryError) {
        if (isRateLimited(retryError)) {
          const stale = getRequestCache.get(endpoint)?.data;
          if (stale !== undefined) {
            rateLimitCooldown.set(endpoint, Date.now() + 15000);
            return stale as T;
          }
          rateLimitCooldown.set(endpoint, Date.now() + 5000);
        }
        throw retryError;
      }
    }
  })()
    .then((data) => {
      getRequestCache.set(endpoint, {
        data,
        expiresAt: Date.now() + ttlMs,
      });
      rateLimitCooldown.delete(endpoint);
      return data;
    })
    .finally(() => {
      const latest = getRequestCache.get(endpoint);
      if (latest?.promise === promise && latest.data === undefined) {
        getRequestCache.delete(endpoint);
      }
    });

  getRequestCache.set(endpoint, {
    expiresAt: now + ttlMs,
    promise,
  });

  return promise;
}

async function fetchWithLocalProxyFallback(endpoint: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(endpoint, init);
  } catch (error) {
    if (!isLocalDevHost()) throw error;

    const localPath = toSameOriginPath(endpoint);
    if (!localPath) throw error;

    try {
      return await fetch(localPath, init);
    } catch {
      throw error;
    }
  }
}

// ----------------- API HELPER -----------------
export async function fetchData<T = unknown>(endpoint: string, options: RequestInit & { headers?: Record<string, string> } = {}): Promise<T> {
  let token = getAccessToken();
  if (!token) throw new Error("No token available");

  const requestInit: RequestInit = {
    ...options,
    headers: {
      ...(options.headers || {}),
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };

  const res = await fetchWithLocalProxyFallback(endpoint, requestInit);

  // Handle 401 - token may be expired
  if (res.status === 401) {
    if (isRefreshing) {
      // Already refreshing - queue this request
      return new Promise((resolve, reject) => {
        queueRequest(
          () => fetchData(endpoint, options).then(resolve).catch(reject),
          reject
        );
      });
    }

    isRefreshing = true;
    try {
      // Try to refresh the access token
      const newToken = await refreshAccessToken();
      isRefreshing = false;
      processPendingRequests();

      // Retry the original request with new token
      const retry = await fetchWithLocalProxyFallback(endpoint, {
        ...requestInit,
        headers: {
          ...(requestInit.headers || {}),
          Authorization: `Bearer ${newToken}`,
        },
      });

      if (!retry.ok) {
        const retryErrorBody = await retry.text();
        throw new Error(`API error ${retry.status}: ${retryErrorBody}`);
      }

      return parseResponseBody<T>(retry);
    } catch (err) {
      isRefreshing = false;
      processPendingRequests(err);
      throw err;
    }
  }

  if (!res.ok) {
    let message = '';
    try {
      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const errorJson = await res.json();
        message = errorJson?.error || errorJson?.message || JSON.stringify(errorJson);
      } else {
        message = await res.text();
      }
    } catch {
      message = `Request failed (${res.status})`;
    }
    throw new Error(`API error ${res.status}: ${message}`);
  }

  return parseResponseBody<T>(res);
}


// ----------------- SITE CRUD -----------------
export async function fetchSites(product: string = 'vetra'): Promise<ISite[]> {
  const all: ISite[] = await fetchData(API.sites.list);
  if (!product) return all;
  return (all || []).filter(s => s.product === product);
}

export async function fetchFleetOverview(product: string = 'vetra'): Promise<IFleetSiteKPI[]> {
  return fetchData<IFleetSiteKPI[]>(API.fleet.overview(product));
}

export async function createSite(data: Partial<ISite>): Promise<ISite> {
  return fetchData(API.sites.create, { method: "POST", body: JSON.stringify(data) });
}

export async function updateSite(id: string, data: Partial<ISite>): Promise<ISite> {
  return fetchData(API.sites.update(id), { method: "PUT", body: JSON.stringify(data) });
}

export async function deleteSite(id: string): Promise<unknown> {
  return fetchData(API.sites.delete(id), { method: "DELETE" });
}

// ----------------- REGISTER CRUD -----------------
export async function fetchRegisters(siteId: string): Promise<IRegister[]> {
  return fetchData(API.registers.listBySite(siteId));
}

export async function fetchRegisterById(registerId: string): Promise<IRegister> {
  return fetchData(API.registers.get(registerId));
}

export async function fetchRegistersAdvanced(siteId: string, filters: Record<string, string> = {}): Promise<IRegister[]> {
  const params = new URLSearchParams();
  
  Object.keys(filters).forEach(key => {
    if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
      params.append(key, filters[key]);
    }
  });

  const url = `${API.registers.advanced(siteId)}?${params.toString()}`;
  return fetchData(url);
}

export async function updateRegister(registerId: string, data: Partial<IRegister>): Promise<IRegister> {
  return fetchData(API.registers.update(registerId), { method: "PUT", body: JSON.stringify(data) });
}

export async function assignRegister(siteId: string, data: Record<string, unknown>): Promise<unknown> {
  return fetchData(API.registers.assign(siteId), { method: "POST", body: JSON.stringify(data) });
}

export async function deleteRegister(registerId: string): Promise<unknown> {
  return fetchData(API.registers.delete(registerId), { method: "DELETE" });
}

export async function deleteRegisterBulk(registerIds: string[]): Promise<unknown[]> {
  return Promise.all(registerIds.map(id => deleteRegister(id)));
}

export async function generateDeviceRegister(deviceId: string): Promise<unknown> {
  const device = await fetchData<any>(API.devices.get(deviceId));
  const siteId = device?.siteId;

  if (!siteId) {
    throw new Error('Unable to resolve siteId from device');
  }

  // Backend currently refreshes/generates register set per site.
  return fetchData(API.registers.refresh(siteId), { method: 'POST' });
}

export async function fetchRegisterHistory(siteId: string, registerId: string, from?: string, to?: string): Promise<{ timestamps: string[]; values: (number | null)[] }> {
  const url = buildUrl(API.registers.history(siteId, registerId));
  if (from) url.searchParams.append("from", from);
  if (to) url.searchParams.append("to", to);
  return fetchData(url.toString());
}

// ─── ASSET HISTORY (Pre-Aggregated) ───
export interface AssetHistoryResponse {
  period: { from: string; to: string };
  interval: string;
  timestamps: string[];
  series: Record<string, (number | null)[]>;
  summary: Record<string, { min: number | null; max: number | null; avg: number | null; total: number | null }>;
}

export async function fetchPVHistory(siteId: string, from?: string, to?: string, range?: string, interval?: string): Promise<AssetHistoryResponse> {
  const url = buildUrl(API.assets.pvHistory(siteId));
  if (from) url.searchParams.append("from", from);
  if (to) url.searchParams.append("to", to);
  if (range) url.searchParams.append("range", range);
  if (interval) url.searchParams.append("interval", interval);
  return fetchData(url.toString());
}

export async function fetchBatteryHistory(siteId: string, from?: string, to?: string, range?: string, interval?: string): Promise<AssetHistoryResponse> {
  const url = buildUrl(API.assets.batteryHistory(siteId));
  if (from) url.searchParams.append("from", from);
  if (to) url.searchParams.append("to", to);
  if (range) url.searchParams.append("range", range);
  if (interval) url.searchParams.append("interval", interval);
  return fetchData(url.toString());
}

export async function fetchGeneratorHistory(siteId: string, from?: string, to?: string, range?: string, interval?: string): Promise<AssetHistoryResponse> {
  const url = buildUrl(API.assets.generatorHistory(siteId));
  if (from) url.searchParams.append("from", from);
  if (to) url.searchParams.append("to", to);
  if (range) url.searchParams.append("range", range);
  if (interval) url.searchParams.append("interval", interval);
  return fetchData(url.toString());
}

export async function fetchGridHistory(siteId: string, from?: string, to?: string, range?: string, interval?: string): Promise<AssetHistoryResponse> {
  const url = buildUrl(API.assets.gridHistory(siteId));
  if (from) url.searchParams.append("from", from);
  if (to) url.searchParams.append("to", to);
  if (range) url.searchParams.append("range", range);
  if (interval) url.searchParams.append("interval", interval);
  return fetchData(url.toString());
}

// ----------------- DEVICES -----------------
export async function postAlarm(alarmData: Partial<IAlarm>): Promise<IAlarm> {
  return fetchData(API.alarm.post, { method: "POST", body: JSON.stringify(alarmData) });
}

type AlarmListFilters = {
  siteId?: string;
  state?: string;
  severity?: string;
  category?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
  skip?: number;
};

function normalizeAlarmState(value?: string): string | undefined {
  if (!value) return undefined;
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'all') return undefined;
  if (normalized === 'resolved') return 'cleared';
  return normalized;
}

export async function fetchAlarms(siteId: string, state?: string, limit?: number, offset?: number): Promise<IAlarm[]>;
export async function fetchAlarms(filters?: AlarmListFilters): Promise<IAlarm[]>;
export async function fetchAlarms(
  siteIdOrFilters: string | AlarmListFilters = {},
  state?: string,
  limit?: number,
  offset?: number,
): Promise<IAlarm[]> {
  const filters: AlarmListFilters = typeof siteIdOrFilters === 'string'
    ? {
        siteId: siteIdOrFilters,
        state: normalizeAlarmState(state),
        limit,
        offset,
      }
    : {
        ...siteIdOrFilters,
        state: normalizeAlarmState(siteIdOrFilters.state),
        offset: siteIdOrFilters.offset ?? siteIdOrFilters.skip,
      };

  const url = new URL(API.alarm.list, window.location.origin);
  Object.entries(filters).forEach(([k, v]) => {
    if (k === 'skip') return;
    if (v !== undefined && v !== null && v !== '') url.searchParams.append(k, String(v));
  });
  return fetchData(url.toString());
}

export async function fetchAlarmCount(siteId?: string, state?: string): Promise<{ count: number }> {
  const url = new URL(API.alarm.count, window.location.origin);
  if (siteId) url.searchParams.append('siteId', siteId);
  if (state) url.searchParams.append('state', normalizeAlarmState(state) || state);
  return fetchData(url.toString());
}

export async function acknowledgeAlarm(alarmId: string): Promise<unknown> {
  return fetchData(API.alarm.acknowledge(alarmId), { method: "PUT" });
}

export async function unacknowledgeAlarm(alarmId: string): Promise<unknown> {
  return fetchData(API.alarm.unacknowledge(alarmId), { method: "PUT" });
}

export async function bulkAcknowledgeAlarms(alarmIds: string[]): Promise<unknown> {
  return fetchData(API.alarm.bulkAcknowledge, { method: "PUT", body: JSON.stringify({ alarmIds }) });
}

export async function clearAlarm(alarmId: string): Promise<unknown> {
  return fetchData(API.alarm.clear(alarmId), { method: "PUT" });
}

export async function fetchAlarmStatistics(siteId?: string): Promise<unknown> {
  const url = new URL(API.alarm.statistics, window.location.origin);
  if (siteId) url.searchParams.append('siteId', siteId);
  return fetchData(url.toString());
}

export async function exportAlarms(filters: Record<string, string> = {}): Promise<unknown> {
  const url = new URL(API.alarm.export, window.location.origin);
  Object.entries(filters).forEach(([k, v]) => url.searchParams.append(k, v));
  return fetchData(url.toString());
}

export async function shelveAlarm(alarmId: string): Promise<unknown> {
  return fetchData(API.alarm.shelve(alarmId), { method: "POST" });
}

export async function unshelveAlarm(alarmId: string): Promise<unknown> {
  return fetchData(API.alarm.unshelve(alarmId), { method: "POST" });
}

// ----------------- DICTIONARY -----------------
export async function fetchRegisterDictionaries(): Promise<unknown[]> {
  return fetchData(API.dictionary.list);
}

export async function createRegisterDictionary(data: Record<string, unknown>): Promise<unknown> {
  return fetchData(API.dictionary.create, { method: "POST", body: JSON.stringify(data) });
}

export async function updateRegisterDictionary(key: string, data: Record<string, unknown>): Promise<unknown> {
  return fetchData(API.dictionary.update(key), { method: "PUT", body: JSON.stringify(data) });
}

export async function deleteRegisterDictionary(key: string): Promise<unknown> {
  return fetchData(API.dictionary.delete(key), { method: "DELETE" });
}

export async function bulkUpsertRegisterDictionaries(entries: unknown[]): Promise<unknown> {
  return fetchData(API.dictionary.bulk, { method: "POST", body: JSON.stringify({ entries }) });
}

// ----------------- INITIALIZATION -----------------
export function initAuthFromStorage(): IUser | null {
  const userStr = localStorage.getItem("user");
  const token = getAccessToken();
  if (userStr && token) {
    return JSON.parse(userStr) as IUser;
  }
  return null;
}

// ----------------- KPI -----------------
export async function fetchKpiCurrent(siteId: string): Promise<unknown> {
  return fetchData(API.kpi.current(siteId));
}

export async function fetchKpiHistory(siteId: string, params: { type?: string; startDate?: string; endDate?: string; limit?: number } = {}): Promise<unknown> {
  const url = new URL(API.kpi.history(siteId), window.location.origin);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined) url.searchParams.append(k, String(v));
  });
  return fetchData(url.toString());
}

export async function fetchKpiLiveHistory(siteId: string, interval?: string): Promise<unknown> {
  const url = new URL(API.kpi.liveHistory(siteId), window.location.origin);
  if (interval) url.searchParams.append('interval', String(interval));
  return fetchData(url.toString());
}

export async function fetchKpiSummary(siteId: string, days?: number): Promise<unknown> {
  const url = new URL(API.kpi.summary(siteId), window.location.origin);
  if (days) url.searchParams.append('days', String(days));
  return fetchData(url.toString());
}

export async function fetchKpiEnergyMix(siteId: string, months?: number): Promise<unknown> {
  const url = new URL(API.kpi.energyMix(siteId), window.location.origin);
  if (months) url.searchParams.append('months', String(months));
  return fetchData(url.toString());
}

export async function fetchKpiPeakDemand(siteId: string, days?: number): Promise<unknown> {
  const url = new URL(API.kpi.peakDemand(siteId), window.location.origin);
  if (days) url.searchParams.append('days', String(days));
  return fetchData(url.toString());
}

// ----------------- ASSETS -----------------
export async function fetchPvAsset(siteId: string): Promise<unknown> {
  return fetchData(API.assets.pv(siteId));
}

export async function fetchPvAssetSummary(siteId: string): Promise<unknown> {
  return fetchDataCached(API.assets.pvSummary(siteId), 20000);
}

export async function fetchBatteryAsset(siteId: string): Promise<unknown> {
  return fetchDataCached(API.assets.battery(siteId), 20000);
}

export async function fetchBatteryAssetSummary(siteId: string): Promise<unknown> {
  return fetchDataCached(API.assets.batterySummary(siteId), 20000);
}

export async function fetchGeneratorAsset(siteId: string): Promise<unknown> {
  return fetchData(API.assets.generator(siteId));
}

export async function fetchGeneratorAssetSummary(siteId: string): Promise<unknown> {
  return fetchDataCached(API.assets.generatorSummary(siteId), 20000);
}

export async function fetchGridAsset(siteId: string): Promise<unknown> {
  return fetchData(API.assets.grid(siteId));
}

export async function fetchGridAssetSummary(siteId: string): Promise<unknown> {
  return fetchDataCached(API.assets.gridSummary(siteId), 20000);
}

// ----------------- WEATHER -----------------
export async function fetchWeather(siteId: string): Promise<unknown> {
  return fetchDataCached(API.weather.current(siteId), 12000);
}

export async function fetchSolarPosition(siteId: string): Promise<unknown> {
  return fetchData(API.weather.solarPosition(siteId));
}

export async function fetchWeatherForecast(siteId: string): Promise<unknown> {
  return fetchData(API.weather.forecast(siteId));
}

// ----------------- TOPOLOGY -----------------
export async function fetchTopology(siteId: string): Promise<unknown> {
  return fetchData(API.topology.get(siteId));
}

export async function updateTopology(siteId: string, data: Record<string, unknown>): Promise<unknown> {
  return fetchData(API.topology.update(siteId), { method: "PUT", body: JSON.stringify(data) });
}

export async function fetchTopologyLive(siteId: string): Promise<unknown> {
  return fetchData(API.topology.live(siteId));
}

// ----------------- SLD RUNTIME -----------------
export async function fetchSldRuntime(siteId: string): Promise<unknown> {
  return fetchData(API.sld.runtime(siteId));
}

export async function fetchSldRuntimeLive(siteId: string): Promise<unknown> {
  return fetchData(API.sld.runtimeLive(siteId));
}

// ----------------- EVENT LOGS -----------------
export async function fetchEventLogs(filters: Record<string, string> = {}): Promise<unknown> {
  const url = new URL(API.eventLogs.list, window.location.origin);
  Object.entries(filters).forEach(([k, v]) => url.searchParams.append(k, v));
  return fetchData(url.toString());
}

export async function createEventLog(data: Record<string, unknown>): Promise<unknown> {
  return fetchData(API.eventLogs.create, { method: "POST", body: JSON.stringify(data) });
}

export async function exportEventLogs(filters: Record<string, string> = {}): Promise<unknown> {
  const url = new URL(API.eventLogs.export, window.location.origin);
  Object.entries(filters).forEach(([k, v]) => url.searchParams.append(k, v));
  return fetchData(url.toString());
}

export async function fetchSequenceOfEvents(siteId: string): Promise<unknown> {
  return fetchData(API.eventLogs.soe(siteId));
}

// ----------------- EMS -----------------
export async function fetchEmsConfig(siteId: string): Promise<unknown> {
  return fetchData(API.ems.config(siteId));
}

export async function setEmsMode(siteId: string, mode: string, reason?: string): Promise<unknown> {
  return fetchData(API.ems.setMode(siteId), { method: "POST", body: JSON.stringify({ mode, reason }) });
}

export async function setEmsParameters(siteId: string, parameters: Record<string, unknown>, reason?: string): Promise<unknown> {
  return fetchData(API.ems.setParameters(siteId), { method: "POST", body: JSON.stringify({ parameters, reason }) });
}

export async function fetchEmsHistory(siteId: string, params: { type?: string; limit?: number } = {}): Promise<unknown> {
  const url = new URL(API.ems.history(siteId), window.location.origin);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined) url.searchParams.append(k, String(v));
  });
  return fetchData(url.toString());
}

// ----------------- CONTROL AUTHORITY -----------------
export async function fetchControlAuthority(deviceId: string): Promise<unknown> {
  return fetchData(API.controlAuthority.get(deviceId));
}

export async function setControlAuthority(deviceId: string, mode: string, reason?: string): Promise<unknown> {
  return fetchData(API.controlAuthority.set(deviceId), { method: "PUT", body: JSON.stringify({ mode, reason }) });
}

export async function fetchControlAuthorityBySite(siteId: string): Promise<unknown> {
  return fetchData(API.controlAuthority.listBySite(siteId));
}

// ----------------- DIAGNOSTICS -----------------
export async function fetchDiagnostics(siteId: string): Promise<unknown> {
  return fetchData(API.diagnostics.get(siteId));
}

// ----------------- SECURITY -----------------
export async function fetchSecurityEvents(params: { page?: number; limit?: number } = {}): Promise<unknown> {
  const url = new URL(API.security.events, window.location.origin);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined) url.searchParams.append(k, String(v));
  });
  return fetchData(url.toString());
}

export async function fetchSecuritySummary(hours?: number): Promise<unknown> {
  const url = new URL(API.security.summary, window.location.origin);
  if (hours) url.searchParams.append('hours', String(hours));
  return fetchData(url.toString());
}

export async function fetchSiteWithData(siteId: string, product?: string): Promise<{ site: ISite; devices: IDevice[] }> {
  const res = await fetchData<{ site: ISite; devices: IDevice[] }>(API.sites.get(siteId));
  if (product && res.site && res.site.product !== product) {
    throw new Error(`Site does not belong to product: ${product}`);
  }
  return res;
}

export async function fetchDevices(siteId: string): Promise<IDevice[]> {
  return fetchData(API.devices.getSites(siteId));
}

export async function createDevice(data: Partial<IDevice>): Promise<IDevice> {
  return fetchData(API.devices.create, { method: "POST", body: JSON.stringify(data) });
}

export async function updateDevice(id: string, data: Partial<IDevice>): Promise<IDevice> {
  return fetchData(API.devices.update(id), { method: "PUT", body: JSON.stringify(data) });
}


export async function deleteDevice(id: string): Promise<unknown> {
  return fetchData(API.devices.delete(id), { method: "DELETE" });
}

export async function fetchDeviceHeartbeat(deviceId: string): Promise<unknown> {
  return fetchData(API.devices.heartbeat(deviceId));
}

export async function fetchDeviceQuality(deviceId: string): Promise<unknown> {
  return fetchData(API.devices.quality(deviceId));
}

export async function getDocuments(siteId: string): Promise<unknown[]> {
  const payload = await fetchData<any>(API.document.get(siteId));
  const documents = Array.isArray(payload?.documents) ? payload.documents : [];

  return documents.map((doc: any) => {
    const fileUrl = doc.svgUrl
      ? new URL(doc.svgUrl, API.sites.list).toString()
      : '';

    return {
      ...doc,
      fileUrl,
      createdAt: doc.createdAt || new Date().toISOString(),
      updatedAt: doc.updatedAt || doc.createdAt || new Date().toISOString(),
      tags: doc.tags || [doc.renderMode, doc.status].filter(Boolean),
      source: doc.source || 'SLD',
      author: doc.createdBy || '',
      lastModifier: doc.updatedBy || '',
      linkType: doc.linkType || 'plant',
      size: doc.size || '',
    };
  });
}

// Backward-compatible aliases used by schematic components
export async function fetchSchematics(siteId: string): Promise<unknown[]> {
  return getDocuments(siteId);
}

export async function uploadSchematic(siteId: string, name: string, file: File): Promise<unknown> {
  const formData = new FormData();
  formData.append('siteId', siteId);
  formData.append('name', name);
  formData.append('document', file);

  const uploaded = await uploadDocument(formData) as any;
  return {
    schematic: uploaded,
    nodesGenerated: uploaded?.elementsFound ?? 0,
  };
}

export async function getSvg(svgUrl: string): Promise<Response> {
  const url = new URL(svgUrl, API.sites.list).toString();
  const token = getAccessToken();

  return fetch(url, {
    method: 'GET',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      Accept: 'image/svg+xml,text/plain,*/*',
    },
  });
}

export async function deleteSiteDocument(id: string): Promise<unknown> {
  return fetchData(API.document.delete(id), { method: "DELETE" });
}

export async function uploadDocument(formData: FormData): Promise<unknown> {
  const token = globalState.token as string | null;
  if (!token) throw new Error("No token available");

  const siteId = String(formData.get('siteId') || '');
  if (!siteId) throw new Error('Missing siteId for upload');

  const endpoint = API.document.upload.replace('{siteId}', encodeURIComponent(siteId));

  // SLD upload endpoint expects `svg` file field and optional `name`.
  const uploadData = new FormData();
  const file = formData.get('document');
  if (file) uploadData.append('svg', file as Blob);
  const name = formData.get('name');
  if (typeof name === 'string' && name.trim()) uploadData.append('name', name.trim());

  const res = await fetch(endpoint, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`
    },
    body: uploadData
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Document upload failed ${res.status}: ${text}`);
  }

  const payload = unwrapApiEnvelope<any>(await res.json());
  const doc = payload?.document || payload;

  return {
    ...doc,
    fileUrl: doc?.svgUrl ? new URL(doc.svgUrl, API.sites.list).toString() : '',
    createdAt: doc?.createdAt || new Date().toISOString(),
    updatedAt: doc?.updatedAt || doc?.createdAt || new Date().toISOString(),
    tags: doc?.tags || [doc?.renderMode, doc?.status].filter(Boolean),
    source: doc?.source || 'SLD',
    author: doc?.createdBy || '',
    lastModifier: doc?.updatedBy || '',
    linkType: doc?.linkType || 'plant',
    size: doc?.size || '',
  };
}

export async function fetchAuditLogs(filters: Record<string, string> = {}): Promise<unknown[]> {
  const url = new URL(API.audit.list, window.location.origin);
  Object.entries(filters).forEach(([k, v]) => url.searchParams.append(k, v));
  return fetchData(url.toString());
}

export async function exportAuditLogs(filters: Record<string, string> = {}): Promise<unknown> {
  const url = new URL(API.audit.export, window.location.origin);
  Object.entries(filters).forEach(([k, v]) => url.searchParams.append(k, v));
  return fetchData(url.toString());
}

export async function fetchCarbonSummary(siteId: string): Promise<unknown> {
  return fetchData(API.carbon.summary(siteId));
}